import { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'debounce';
import { useLocation } from 'react-router-dom';
import { compact, deferUntilNewTitle } from '@moda/portal-stanchions';
import { ProductListPageResultFragment_variants_edges_node as VariantCell } from '../generated/types/ProductListPageResultFragment';
import { tc } from '../lib/trackingContext';
import { useLazyCells } from './useLazyCells';
import { usePageType } from './usePageType';

const DEBOUNCE_AMOUNT_MS = 500;

export const usePLPTracking = ({ variants, title }: { variants: VariantCell[]; title: string }) => {
  const { pathname } = useLocation();

  const updatedVariants = useMemo(
    () =>
      variants.map((variant, index) => {
        return {
          ...variant,
          position: index + 1,
          category: `${variant.category[0]}/${variant.subcategory[0]}`
        };
      }),
    [variants]
  );

  const { initialCells, lazyCells } = useLazyCells(updatedVariants);
  const trackedLazyCellIds = useRef<(string | null)[]>([]);
  const pagetype = usePageType();

  useEffect(() => {
    deferUntilNewTitle(() => {
      if (initialCells && initialCells.length)
        tc.track('Product List Viewed', {
          addToScopeOnce: { products: initialCells },
          addToTrackingEvent: {
            pagetype,
            label: title,
            lazyLoaded: false,
            list_id: pathname
          }
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCells, pagetype, title]);

  useEffect(() => {
    trackedLazyCellIds.current = initialCells.map(cell => cell.id);
  }, [trackedLazyCellIds, initialCells]);

  const handleScroll = useCallback(() => {
    const loadedVariantIds = Array.from(document.querySelectorAll('.VariantCell')).map(cell =>
      cell.getAttribute('data-variant-pk')
    );

    const newVariantIds = compact(loadedVariantIds).filter(
      id => !trackedLazyCellIds?.current?.includes(id)
    );

    const newVariants = lazyCells.filter(cell => newVariantIds.includes(cell.id));

    if (newVariants.length > 0) {
      tc.track('Product List Viewed', {
        addToScopeOnce: { products: newVariants },
        addToTrackingEvent: {
          pagetype,
          label: title,
          lazyLoaded: true,
          list_id: pathname
        }
      });
    }

    trackedLazyCellIds.current = [...trackedLazyCellIds.current, ...newVariantIds];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedLazyCellIds, lazyCells, pagetype, title]);

  const debouncedHandleScroll = useMemo(
    () => debounce(handleScroll, DEBOUNCE_AMOUNT_MS),
    [handleScroll]
  );

  useEffect(() => {
    window.addEventListener('scroll', debouncedHandleScroll);
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, [debouncedHandleScroll]);
};
